import React, { useEffect, useMemo, useState } from 'react';
import { toastr } from 'react-redux-toastr';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faMotorcycle, faTruck, faCogs, faCheck } from '@fortawesome/free-solid-svg-icons';

import { useProvider } from './ProviderContext';
import { branchHasServices, loadConfig, loadConfigOptions, saveConfig, validateConfig } from './providerData';

const SEGMENT_ICONS = { Carros: faCar, Motos: faMotorcycle, Pesados: faTruck, Outros: faCogs };

const toggle = (list, id) => (list.includes(id) ? list.filter((item) => item !== id) : [...list, id]);

const sameConfig = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const normalizeForCompare = (config) => ({
  ...config,
  segments: [...config.segments].sort((x, y) => x - y),
  services: [...config.services].sort((x, y) => x - y),
});

// O que a oficina atende: tipos de veiculo, ramo, servicos, horario e site.
// Sempre editavel; a barra de salvar aparece quando algo mudou.
export default function ServicesPage() {
  const { company } = useProvider();
  const [options, setOptions] = useState(null);
  const [saved, setSaved] = useState(null);
  const [config, setConfig] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!company) return undefined;
    let active = true;
    setFailed(false);
    Promise.all([loadConfigOptions(), loadConfig(company)])
      .then(([loadedOptions, loadedConfig]) => {
        if (!active) return;
        setOptions(loadedOptions);
        setSaved(loadedConfig);
        setConfig(loadedConfig);
      })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [company, attempt]);

  const dirty = useMemo(
    () => Boolean(config && saved) && !sameConfig(normalizeForCompare(config), normalizeForCompare(saved)),
    [config, saved],
  );

  const update = (patch) => setConfig((previous) => ({ ...previous, ...patch }));

  const handleSave = async () => {
    const problem = validateConfig(config);
    if (problem) {
      toastr.warning(problem);
      return;
    }
    setSaving(true);
    try {
      await saveConfig(company, config);
      setSaved(config);
      toastr.success('Atendimento atualizado.');
    } catch (err) {
      const message = err && err.response && err.response.data && err.response.data.message;
      toastr.error('Não foi possível salvar.', message || 'Tente de novo em instantes.');
    } finally {
      setSaving(false);
    }
  };

  const showServices = config && branchHasServices(config.branchId);

  return (
    <div className='gs-stack pv-has-savebar'>
      <header className='gs-page-header'>
        <span className='gs-eyebrow'>Atendimento</span>
        <h1>O que sua oficina atende</h1>
        <p>É assim que os clientes encontram a sua oficina na busca do Sobre Rodas.</p>
      </header>

      {failed && (
        <p className='gs-notice gs-notice-error' role='alert'>
          Não foi possível carregar o atendimento.
          <button type='button' className='gs-link' onClick={() => setAttempt((count) => count + 1)}>Tentar de novo</button>
        </p>
      )}

      {!failed && !config && <div className='gs-skeleton' aria-busy='true' aria-label='Carregando' />}

      {config && options && (
        <>
          <section className='pv-panel' aria-labelledby='pv-seg-title'>
            <h2 id='pv-seg-title' className='pv-panel-title'>Tipos de veículo</h2>
            <div className='pv-choices'>
              {options.segments.map((segment) => {
                const selected = config.segments.includes(Number(segment.id));
                return (
                  <button
                    key={segment.id}
                    type='button'
                    className={`pv-choice${selected ? ' is-selected' : ''}`}
                    aria-pressed={selected}
                    onClick={() => update({ segments: toggle(config.segments, Number(segment.id)) })}
                  >
                    <FontAwesomeIcon icon={SEGMENT_ICONS[segment.description] || faCogs} aria-hidden='true' />
                    {segment.description}
                  </button>
                );
              })}
            </div>
          </section>

          <section className='pv-panel' aria-labelledby='pv-branch-title'>
            <h2 id='pv-branch-title' className='pv-panel-title'>Ramo de atuação</h2>
            <div className='pv-choices' role='radiogroup' aria-labelledby='pv-branch-title'>
              {options.branches.map((branch) => {
                const selected = config.branchId === Number(branch.id);
                return (
                  <button
                    key={branch.id}
                    type='button'
                    role='radio'
                    aria-checked={selected}
                    className={`pv-choice${selected ? ' is-selected' : ''}`}
                    onClick={() => update({ branchId: Number(branch.id) })}
                  >
                    {branch.description}
                  </button>
                );
              })}
            </div>
          </section>

          {showServices && (
            <section className='pv-panel' aria-labelledby='pv-serv-title'>
              <h2 id='pv-serv-title' className='pv-panel-title'>
                Serviços
                <span className='pv-panel-count'>{config.services.length} de {options.services.length}</span>
              </h2>
              <div className='pv-tags'>
                {options.services.map((service) => {
                  const selected = config.services.includes(Number(service.id));
                  return (
                    <button
                      key={service.id}
                      type='button'
                      className={`pv-tag${selected ? ' is-selected' : ''}`}
                      aria-pressed={selected}
                      onClick={() => update({ services: toggle(config.services, Number(service.id)) })}
                    >
                      {selected && <FontAwesomeIcon icon={faCheck} aria-hidden='true' />}
                      {service.description}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section className='pv-panel' aria-labelledby='pv-hours-title'>
            <h2 id='pv-hours-title' className='pv-panel-title'>Horário e site</h2>
            <div className='pv-fields pv-fields-hours'>
              <label className='pv-field'>
                <span>Abre às</span>
                <input type='time' value={config.openAt} onChange={(event) => update({ openAt: event.target.value })} />
              </label>
              <label className='pv-field'>
                <span>Fecha às</span>
                <input type='time' value={config.closesAt} onChange={(event) => update({ closesAt: event.target.value })} />
              </label>
              <label className='pv-field pv-field-wide'>
                <span>Site ou página da oficina</span>
                <input
                  type='url'
                  inputMode='url'
                  placeholder='https://'
                  value={config.site}
                  onChange={(event) => update({ site: event.target.value.trim() })}
                />
              </label>
            </div>
          </section>
        </>
      )}

      {dirty && (
        <div className='pv-savebar' role='region' aria-label='Alterações não salvas'>
          <span>Alterações não salvas</span>
          <div>
            <button type='button' className='gs-btn gs-btn-secondary' onClick={() => setConfig(saved)} disabled={saving}>Desfazer</button>
            <button type='button' className='gs-btn gs-btn-primary' onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
